import axios from "axios";

// Admin directory/report data is prefetched before navigation. Mutations clear
// this cache, so a longer read window keeps page transitions instant without
// leaving edited data stale.
// Keep cache window short (5 seconds) so live updates reflect immediately.
const CACHE_TTL_MS = 5 * 1000; // 5 seconds for live data freshness
const CACHE_PREFIX = "admin_api_get_cache:";
const pendingRequests = new Map();
const memoryResponses = new Map();

// Immediately purge old cache items on initialization
try {
  Object.keys(sessionStorage)
    .filter((key) => key.startsWith(CACHE_PREFIX))
    .forEach((key) => sessionStorage.removeItem(key));
} catch {}

const defaultAdapter = axios.getAdapter(axios.defaults.adapter);

const stableParams = (value) => {
  if (!value || typeof value !== "object") return value || null;
  if (Array.isArray(value)) return value.map(stableParams);

  return Object.keys(value)
    .sort()
    .reduce((acc, key) => {
      acc[key] = stableParams(value[key]);
      return acc;
    }, {});
};

const getCacheKey = (config) => {
  const token = localStorage.getItem("admin_token") || "";
  return `${CACHE_PREFIX}${JSON.stringify({
    token,
    baseURL: config.baseURL || "",
    url: config.url || "",
    params: stableParams(config.params),
  })}`;
};

const isCacheableGet = (config) => {
  const method = String(config.method || "get").toLowerCase();
  const url = String(config.url || "");
  const uncachedRoutes = [
    "/auth/me",
    "/company",
    "/superadmin-requests",
    "/employees",
    "/control-center",
    "/notifications",
    "/dashboard"
  ];
  const isUncached = uncachedRoutes.some((route) => url.includes(route));
  return method === "get" && config.cache !== false && !isUncached;
};

const readCache = (key, ttlMs = CACHE_TTL_MS) => {
  const memoryCached = memoryResponses.get(key);
  if (memoryCached && Date.now() - memoryCached.savedAt <= ttlMs) {
    return memoryCached.response;
  }
  if (memoryCached) memoryResponses.delete(key);

  try {
    const cached = JSON.parse(sessionStorage.getItem(key) || "null");
    if (!cached || Date.now() - cached.savedAt > ttlMs) return null;
    memoryResponses.set(key, cached);
    return cached.response;
  } catch {
    sessionStorage.removeItem(key);
    return null;
  }
};

const writeCache = (key, response) => {
  const cachedValue = {
    savedAt: Date.now(),
    response: {
      data: response.data,
      status: response.status,
      statusText: response.statusText,
      headers: response.headers,
    },
  };
  memoryResponses.set(key, cachedValue);

  try {
    sessionStorage.setItem(key, JSON.stringify(cachedValue));
  } catch {
    // Storage can be full or disabled; the request itself should still succeed.
  }
};

export const clearApiCache = () => {
  Object.keys(sessionStorage)
    .filter((key) => key.startsWith(CACHE_PREFIX))
    .forEach((key) => sessionStorage.removeItem(key));
  pendingRequests.clear();
  memoryResponses.clear();
};

export const createCacheAdapter = () => async (config) => {
  if (!isCacheableGet(config)) {
    return defaultAdapter(config);
  }

  const key = getCacheKey(config);
  const ttlMs = Number(config.cacheTtlMs) > 0 ? Number(config.cacheTtlMs) : CACHE_TTL_MS;
  const cached = readCache(key, ttlMs);
  if (cached) {
    return {
      ...cached,
      config,
      request: null,
      cached: true,
    };
  }

  if (pendingRequests.has(key)) {
    return pendingRequests.get(key);
  }

  const request = defaultAdapter(config)
    .then((response) => {
      writeCache(key, response);
      return response;
    })
    .finally(() => {
      pendingRequests.delete(key);
    });

  pendingRequests.set(key, request);
  return request;
};
